import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useEffect, useRef } from 'react';
import { MessageSquare, ShoppingCart, CreditCard, Check, Loader2 } from 'lucide-react';
import { marked } from 'marked';
import { wac } from '../utils/wacClassNames';
import { ProductCarousel } from './ProductCarousel';
import { CartView } from './CartView';
import { useTranslation } from '../../hooks/useTranslation';
import { updateCartCountInHeader } from '../../utils/cart';
import { trackEcommerce } from '../../analytics';
import chatService from '../../services/chatService';
/**
 * Configure marked for safe rendering inside chat bubbles.
 * Supports: bold, italic, code, links, tables, lists, newlines.
 */
marked.setOptions({
    gfm: true, // GitHub Flavored Markdown (tables, strikethrough)
    breaks: true, // Convert \n to <br>
});
// Custom renderer to add inline styles (no external CSS needed for security)
/* eslint-disable @typescript-eslint/no-explicit-any */
const renderer = {
    heading({ text, depth }) {
        const sizes = { 1: '1.3em', 2: '1.15em', 3: '1.05em', 4: '1em', 5: '0.95em', 6: '0.9em' };
        const size = sizes[depth] || '1em';
        return `<strong style="display:block;font-size:${size};margin:4px 0 2px">${text}</strong>`;
    },
    link({ href, text }) {
        return `<a href="${href}" target="_blank" rel="noopener noreferrer" style="color:var(--waa-primary);text-decoration:underline">${text}</a>`;
    },
    codespan({ text }) {
        return `<code style="background:rgba(0,0,0,0.08);padding:0.1em 0.3em;border-radius:3px;font-family:monospace;font-size:0.9em">${text}</code>`;
    },
    table(token) {
        // Build header row
        const headerCells = token.header.map((cell) => `<th style="padding:6px 10px;text-align:start;font-weight:600;background:rgba(0,0,0,0.04);white-space:nowrap">${cell.text}</th>`).join('');
        const headerRow = `<tr style="border-bottom:2px solid rgba(0,0,0,0.15)">${headerCells}</tr>`;
        // Build body rows
        const bodyRows = token.rows.map((row) => {
            const cells = row.map((cell) => `<td style="padding:6px 10px;text-align:start;white-space:normal">${cell.text}</td>`).join('');
            return `<tr style="border-bottom:1px solid rgba(0,0,0,0.08)">${cells}</tr>`;
        }).join('');
        return `<div style="overflow-x:auto;margin:8px 0"><table style="border-collapse:collapse;width:100%;font-size:13px"><thead>${headerRow}</thead><tbody>${bodyRows}</tbody></table></div>`;
    },
};
/* eslint-enable @typescript-eslint/no-explicit-any */
marked.use({ renderer });
/**
 * Parse markdown into safe HTML using the marked library.
 */
function parseMarkdown(text) {
    try {
        return marked.parse(text);
    }
    catch {
        // Fallback: escape HTML and convert newlines
        return text
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/\n/g, '<br>');
    }
}
export function ChatMessage({ message, onAddToCart, sessionId }) {
    const { t, isRTL, direction } = useTranslation();
    if (message.type === 'system') {
        return (_jsx("div", { className: wac('flex justify-center'), children: _jsx("div", { dir: direction, className: wac('px-3 py-2 rounded-lg text-xs text-center'), style: {
                    background: 'var(--waa-chat-system-bg)',
                    color: 'var(--waa-gray-600)',
                    whiteSpace: 'pre-wrap',
                    maxWidth: '80%',
                }, children: message.content }) }));
    }
    if (message.type === 'user') {
        // In RTL: user messages align left, in LTR: user messages align right
        const userAlign = isRTL ? 'justify-start' : 'justify-end';
        const userItemsAlign = isRTL ? 'items-start' : 'items-end';
        const userRoundedCorner = isRTL ? 'rounded-bl-md' : 'rounded-br-md';
        return (_jsx("div", { className: wac(`flex ${userAlign}`), children: _jsxs("div", { className: wac(`flex flex-col ${userItemsAlign}`), style: { maxWidth: '75%' }, children: [message.image && (_jsx("img", { src: `data:image/jpeg;base64,${message.image}`, alt: "Uploaded", style: { maxHeight: '120px', width: 'auto', objectFit: 'contain', borderRadius: '8px', marginBottom: '4px' } })), _jsx("div", { dir: direction, className: wac(`px-4 py-3 rounded-2xl ${userRoundedCorner} text-[15px] leading-relaxed`), style: {
                            background: 'var(--waa-chat-user-bg)',
                            color: 'var(--waa-chat-user-text)',
                            whiteSpace: 'pre-wrap',
                        }, children: message.content }), message.timestamp && (_jsx("div", { className: wac('text-[11px] mt-1'), style: { color: 'var(--waa-gray-500)' }, children: message.timestamp }))] }) }));
    }
    // Bot message
    // Check if we have cart data from tool_results (get_cart tool) OR cart directly in message
    const cartToolResult = message.tool_results?.find((tr) => tr.tool === 'get_cart' && tr.result?.cart);
    const initialCartData = cartToolResult?.result?.cart || message.cart;
    const [liveCartData, setLiveCartData] = useState(undefined);
    const cartData = liveCartData || initialCartData;
    const hasCart = !!cartData && Array.isArray(cartData.items);
    const config = window.wooAIChatConfig;
    const cartUrl = config?.cartUrl || '';
    const checkoutUrl = config?.checkoutUrl || '';
    // Real cart action execution (with de-duplication guard against React remounts)
    const hasCartActions = Array.isArray(message.cart_actions) && message.cart_actions.length > 0;
    const [cartStatus, setCartStatus] = useState('idle');
    const [cartFeedback, setCartFeedback] = useState(null);
    const cartActionsExecutedRef = useRef(false);
    useEffect(() => {
        console.log('[WooAI] CartAction effect check:', { hasCartActions, cartStatus, executed: cartActionsExecutedRef.current, actions: message.cart_actions });
        if (!hasCartActions || cartStatus !== 'idle' || cartActionsExecutedRef.current)
            return;
        cartActionsExecutedRef.current = true;
        const executeCartActions = async () => {
            setCartStatus('adding');
            setCartFeedback({ status: 'success', message: '🔄 Processing cart operation...' });
            const storeNonce = window.wooAIChatConfig?.storeApiNonce || '';
            let hadAdd = false;
            let hadRemove = false;
            try {
                for (const action of message.cart_actions) {
                    if (action.action === 'add') {
                        hadAdd = true;
                        console.log('[WooAI] Cart add:', { product_id: action.product_id, variation_id: action.variation_id, quantity: action.quantity });
                        // Try WC AJAX add_to_cart first
                        const formData = new FormData();
                        formData.append('product_id', String(action.product_id));
                        formData.append('quantity', String(action.quantity || 1));
                        if (action.variation_id) {
                            formData.append('variation_id', String(action.variation_id));
                        }
                        if (action.variation) {
                            for (const [key, val] of Object.entries(action.variation)) {
                                const normalizedKey = key.startsWith('attribute_') ? key : `attribute_${key}`;
                                formData.append(normalizedKey, val);
                            }
                        }
                        const ajaxResp = await fetch('/?wc-ajax=add_to_cart', {
                            method: 'POST',
                            body: formData,
                            credentials: 'same-origin',
                        });
                        const ajaxResult = await ajaxResp.json().catch(() => null);
                        console.log('[WooAI] wc-ajax add_to_cart result:', ajaxResult);
                        // Apply fragments from successful wc-ajax response to update mini-cart
                        if (ajaxResult && !ajaxResult.error && ajaxResult.fragments) {
                            const jq = window.jQuery;
                            if (jq) {
                                jq.each(ajaxResult.fragments, function (key, value) {
                                    jq(key).replaceWith(value);
                                });
                                jq(document.body).trigger('added_to_cart', [ajaxResult.fragments, ajaxResult.cart_hash]);
                            }
                        }
                        // If WC AJAX failed, fallback to Store API add-item
                        if (!ajaxResult || ajaxResult.error) {
                            console.warn('[WooAI] wc-ajax failed, trying Store API add-item');
                            const storePayload = {
                                id: action.variation_id || action.product_id,
                                quantity: action.quantity || 1,
                            };
                            if (action.variation) {
                                storePayload.variation = Object.entries(action.variation).map(([key, val]) => ({
                                    attribute: key.replace(/^attribute_/, ''),
                                    value: val,
                                }));
                            }
                            // Always fetch a fresh nonce before Store API add-item.
                            // The page-load nonce (storeNonce) may be stale on cached pages (e.g. Breeze/Varnish).
                            // GET /cart does not require a nonce and returns a fresh one in the Nonce response header.
                            let freshNonce = storeNonce;
                            try {
                                const nonceResp = await fetch('/wp-json/wc/store/v1/cart', { credentials: 'same-origin' });
                                const headerNonce = nonceResp.headers.get('Nonce') || nonceResp.headers.get('X-WC-Store-API-Nonce');
                                if (headerNonce) {
                                    freshNonce = headerNonce;
                                    console.log('[WooAI] Refreshed Store API nonce for add-item');
                                }
                            }
                            catch (_) {
                                // Keep storeNonce as fallback if cart fetch fails
                            }
                            const storeResp = await fetch('/wp-json/wc/store/v1/cart/add-item', {
                                method: 'POST',
                                headers: { 'Content-Type': 'application/json', 'Nonce': freshNonce },
                                body: JSON.stringify(storePayload),
                                credentials: 'same-origin',
                            });
                            const storeResult = await storeResp.json().catch(() => null);
                            console.log('[WooAI] Store API add-item result:', storeResult);
                            // Trigger fragment refresh and added_to_cart for Store API fallback.
                            // Use both events: wc_fragment_refresh (standard WC) and added_to_cart
                            // (needed by some themes like Donkey that use custom fragment handlers).
                            const jqFallback = window.jQuery;
                            if (jqFallback) {
                                const fragments = storeResult?.fragments || {};
                                jqFallback(document.body).trigger('added_to_cart', [fragments, storeResult?.cart_hash || '']);
                                jqFallback(document.body).trigger('wc_fragment_refresh');
                            }
                            // For themes with custom fragment handlers (e.g. Donkey uses get_refreshed_fragments
                            // via admin-ajax), call window.updateFragments if available to update the mini-cart UI.
                            // We call admin-ajax get_refreshed_fragments which returns theme-specific fragment HTML.
                            if (storeResult && !storeResult.code) {
                                try {
                                    const fragFormData = new FormData();
                                    fragFormData.append('action', 'get_refreshed_fragments');
                                    const fragResp = await fetch('/wp-admin/admin-ajax.php', {
                                        method: 'POST',
                                        body: fragFormData,
                                        credentials: 'same-origin',
                                    });
                                    const fragData = await fragResp.json().catch(() => null);
                                    if (fragData?.success && fragData?.data?.fragments && window.updateFragments) {
                                        window.updateFragments(fragData.data.fragments);
                                        console.log('[WooAI] Updated cart fragments via get_refreshed_fragments');
                                    }
                                }
                                catch (_) {
                                    // Non-critical — fragment refresh failure doesn't affect cart state
                                }
                            }
                        }
                        // Track cart add for analytics funnel
                        try {
                            trackEcommerce('add_to_cart', [{
                                    id: String(action.product_id),
                                    name: action.product_name || `Product ${action.product_id}`,
                                    price: 0,
                                    quantity: Number(action.quantity) || 1,
                                }], {
                                label: 'ai_chat',
                                custom: { source: 'ai_chat', session_id: sessionId || undefined },
                            });
                            if (sessionId) {
                                chatService.trackEvent(sessionId, 'cart_added', {
                                    product_id: action.product_id,
                                    variation_id: action.variation_id || null,
                                    quantity: action.quantity || 1,
                                    source: 'ai_chat',
                                });
                            }
                        }
                        catch (e) {
                            // Tracking must never break cart logic
                            console.warn('[WooAI] Cart add tracking failed:', e);
                        }
                    }
                    else if (action.action === 'remove' && (action.product_id || action.cart_item_key || action.product_name)) {
                        hadRemove = true;
                        console.log('[WooAI] Cart remove:', { product_id: action.product_id, variation_id: action.variation_id, cart_item_key: action.cart_item_key });
                        try {
                            // Fetch current cart WITHOUT stale nonce (GET doesn't need it, uses session cookie).
                            // Sending a stale nonce (from Varnish-cached page) causes WC to reject the request.
                            const cartResp = await fetch('/wp-json/wc/store/v1/cart', {
                                credentials: 'same-origin',
                            });
                            const freshNonce = cartResp.headers.get('Nonce') || cartResp.headers.get('X-WC-Store-API-Nonce') || storeNonce;
                            const cartData = await cartResp.json();
                            const items = cartData.items || [];
                            console.log('[WooAI] Current cart items:', items.map((i) => ({ id: i.id, name: i.name, key: i.key })));
                            // Match by cart_item_key first (most reliable)
                            let item = action.cart_item_key
                                ? items.find((i) => i.key === action.cart_item_key)
                                : null;
                            // Match by exact id (simple products or when AI sends variation_id as product_id)
                            if (!item && action.product_id) {
                                item = items.find((i) => i.id === action.product_id);
                            }
                            // Match by variation_id
                            if (!item && action.variation_id) {
                                item = items.find((i) => i.id === action.variation_id);
                            }
                            // Match by parent product_id in extensions (variable products: Store API id = variation_id,
                            // but AI sends parent product_id - this is the most common mismatch)
                            if (!item && action.product_id) {
                                item = items.find((i) => {
                                    const ext = i.extensions || {};
                                    return ext.woocommerce_product_id === action.product_id
                                        || ext.product_id === action.product_id
                                        || (i.parent_id && i.parent_id === action.product_id);
                                });
                            }
                            // Fuzzy name match (handles case differences, extra whitespace, HTML entities)
                            if (!item && action.product_name && action.product_name !== 'product') {
                                const searchName = action.product_name.toLowerCase().trim();
                                item = items.find((i) => {
                                    const itemName = (i.name || '').toLowerCase().trim();
                                    return itemName === searchName || itemName.includes(searchName) || searchName.includes(itemName);
                                });
                            }
                            // Last resort: if only 1 item in cart and no match found, remove it
                            // (user said "remove the product" without specifying which one)
                            if (!item && items.length === 1) {
                                item = items[0];
                                console.log('[WooAI] Remove: single item fallback -', item.name);
                            }
                            if (item?.key) {
                                setCartFeedback({ status: 'success', message: `🔄 Removing "${item.name}" (key: ${item.key.substring(0, 8)}...)` });
                                const removeResp = await fetch('/wp-json/wc/store/v1/cart/remove-item', {
                                    method: 'POST',
                                    headers: { 'Content-Type': 'application/json', 'Nonce': freshNonce },
                                    body: JSON.stringify({ key: item.key }),
                                    credentials: 'same-origin',
                                });
                                const removeResult = await removeResp.json().catch(() => null);
                                console.log('[WooAI] Remove result:', removeResult);
                                if (removeResult?.code) {
                                    setCartFeedback({ status: 'error', message: `❌ Remove failed: ${removeResult.message || removeResult.code}` });
                                }
                                // Update CartView with fresh data after removal
                                if (removeResult && !removeResult.code) {
                                    const updatedNonce = removeResp.headers.get('Nonce') || freshNonce;
                                    const freshCart = await fetch('/wp-json/wc/store/v1/cart', {
                                        credentials: 'same-origin',
                                        headers: { 'Nonce': updatedNonce },
                                    }).then(r => r.json()).catch(() => null);
                                    if (freshCart) {
                                        const minorUnit = freshCart.totals?.currency_minor_unit ?? 2;
                                        const divisor = Math.pow(10, minorUnit);
                                        setLiveCartData({
                                            items: freshCart.items || [],
                                            total: freshCart.totals?.total_price || '0',
                                            total_raw: parseFloat(freshCart.totals?.total_price || '0') / divisor,
                                            subtotal: freshCart.totals?.total_items || '0',
                                            subtotal_raw: parseFloat(freshCart.totals?.total_items || '0') / divisor,
                                            count: freshCart.items_count || 0,
                                            cart_url: freshCart.extensions?.cart_url || '',
                                            checkout_url: freshCart.extensions?.checkout_url || '',
                                        });
                                    }
                                    // Track cart remove for analytics funnel
                                    try {
                                        trackEcommerce('remove_from_cart', [{
                                                id: String(action.product_id || item.id || '0'),
                                                name: action.product_name || item.name || `Product ${action.product_id}`,
                                                price: 0,
                                                quantity: Number(action.quantity) || 1,
                                            }], {
                                            label: 'ai_chat',
                                            custom: { source: 'ai_chat', session_id: sessionId || undefined },
                                        });
                                        if (sessionId) {
                                            chatService.trackEvent(sessionId, 'cart_removed', {
                                                product_id: action.product_id || null,
                                                variation_id: action.variation_id || null,
                                                quantity: action.quantity || 1,
                                                source: 'ai_chat',
                                            });
                                        }
                                    }
                                    catch (e) {
                                        // Tracking must never break cart logic
                                        console.warn('[WooAI] Cart remove tracking failed:', e);
                                    }
                                }
                            }
                            else {
                                console.warn('[WooAI] No item found in cart to remove. product_id=' + action.product_id + ' items=' + items.length);
                                setCartFeedback({ status: 'error', message: `❌ No matching item (${items.length} items in cart, looking for pid=${action.product_id} name="${action.product_name}")` });
                            }
                        }
                        catch (e) {
                            console.warn('[WooAI] Remove from cart failed:', e);
                            setCartFeedback({ status: 'error', message: `❌ Remove error: ${e}` });
                        }
                    }
                    else if (action.action === 'clear') {
                        hadRemove = true;
                        console.log('[WooAI] Cart clear: removing all items');
                        try {
                            const cartResp = await fetch('/wp-json/wc/store/v1/cart', {
                                credentials: 'same-origin',
                            });
                            let clearNonce = cartResp.headers.get('Nonce') || cartResp.headers.get('X-WC-Store-API-Nonce') || storeNonce;
                            const cartItems = await cartResp.json();
                            const items = cartItems.items || [];
                            for (const item of items) {
                                if (item.key) {
                                    const removeResp = await fetch('/wp-json/wc/store/v1/cart/remove-item', {
                                        method: 'POST',
                                        headers: { 'Content-Type': 'application/json', 'Nonce': clearNonce },
                                        body: JSON.stringify({ key: item.key }),
                                        credentials: 'same-origin',
                                    });
                                    // WC rotates nonce after each POST - must use fresh one for next call
                                    const nextNonce = removeResp.headers.get('Nonce') || removeResp.headers.get('X-WC-Store-API-Nonce');
                                    if (nextNonce)
                                        clearNonce = nextNonce;
                                    console.log('[WooAI] Removed item:', item.name);
                                }
                            }
                            // Update internal cart state to empty
                            setLiveCartData({
                                items: [],
                                total: '0',
                                total_raw: 0,
                                subtotal: '0',
                                subtotal_raw: 0,
                                count: 0,
                                cart_url: '',
                                checkout_url: '',
                            });
                        }
                        catch (e) {
                            console.warn('[WooAI] Clear cart failed:', e);
                        }
                    }
                    else if (action.action === 'update_quantity' && (action.product_id || action.cart_item_key)) {
                        hadRemove = true; // triggers fragment refresh
                        console.log('[WooAI] Cart update quantity:', { product_id: action.product_id, quantity: action.quantity, cart_item_key: action.cart_item_key });
                        try {
                            const cartResp = await fetch('/wp-json/wc/store/v1/cart', {
                                credentials: 'same-origin',
                            });
                            const freshNonce = cartResp.headers.get('Nonce') || cartResp.headers.get('X-WC-Store-API-Nonce') || storeNonce;
                            const cartData = await cartResp.json();
                            const items = cartData.items || [];
                            // Match item - same logic as remove
                            let item = action.cart_item_key
                                ? items.find((i) => i.key === action.cart_item_key)
                                : null;
                            if (!item && action.product_id) {
                                item = items.find((i) => i.id === action.product_id);
                            }
                            if (!item && action.product_id) {
                                item = items.find((i) => {
                                    const ext = i.extensions || {};
                                    return ext.woocommerce_product_id === action.product_id
                                        || ext.product_id === action.product_id
                                        || (i.parent_id && i.parent_id === action.product_id);
                                });
                            }
                            if (!item && action.product_name && action.product_name !== 'product') {
                                const searchName = action.product_name.toLowerCase().trim();
                                item = items.find((i) => {
                                    const itemName = (i.name || '').toLowerCase().trim();
                                    return itemName === searchName || itemName.includes(searchName) || searchName.includes(itemName);
                                });
                            }
                            // Single item fallback - if only 1 item in cart, use it
                            if (!item && items.length === 1) {
                                item = items[0];
                                console.log('[WooAI] Update qty: single item fallback -', item.name);
                            }
                            if (item?.key) {
                                const qty = action.quantity ?? 1;
                                if (qty === 0) {
                                    // Remove if quantity is 0
                                    await fetch('/wp-json/wc/store/v1/cart/remove-item', {
                                        method: 'POST',
                                        headers: { 'Content-Type': 'application/json', 'Nonce': freshNonce },
                                        body: JSON.stringify({ key: item.key }),
                                        credentials: 'same-origin',
                                    });
                                }
                                else {
                                    await fetch('/wp-json/wc/store/v1/cart/update-item', {
                                        method: 'POST',
                                        headers: { 'Content-Type': 'application/json', 'Nonce': freshNonce },
                                        body: JSON.stringify({ key: item.key, quantity: qty }),
                                        credentials: 'same-origin',
                                    });
                                }
                                console.log('[WooAI] Updated quantity for', item.name, 'to', qty);
                            }
                            else {
                                console.warn('[WooAI] No item found in cart to update quantity. product_id=' + action.product_id);
                            }
                        }
                        catch (e) {
                            console.warn('[WooAI] Update quantity failed:', e);
                        }
                    }
                }
                // Wait for WC session writes to propagate before refreshing fragments (300ms prevents race condition)
                await new Promise(resolve => setTimeout(resolve, 300));
                // Force-refresh cart after any cart operation.
                // Fetches fresh fragments via WC AJAX and applies them using
                // multiple methods to support different themes.
                try {
                    const fragResp = await fetch('/?wc-ajax=get_refreshed_fragments', {
                        method: 'POST',
                        credentials: 'same-origin',
                    });
                    const fragData = await fragResp.json().catch(() => null);
                    if (fragData?.fragments) {
                        // Method 1: Theme's own updateFragments (e.g. Donkey Table theme)
                        const updateFn = window.updateFragments;
                        if (typeof updateFn === 'function') {
                            updateFn(fragData.fragments);
                            console.log('[WooAI] Cart updated via theme updateFragments');
                        }
                        // Method 2: jQuery fragment replacement (standard WC themes)
                        const jqFrag = window.jQuery;
                        if (jqFrag) {
                            jqFrag.each(fragData.fragments, function (key, value) {
                                jqFrag(key).replaceWith(value);
                            });
                        }
                    }
                }
                catch (e) {
                    console.warn('[WooAI] Fragment refresh failed:', e);
                }
                // Donkey theme has a custom cart drawer (div.cart-block) that is NOT registered
                // as a WC fragment. It has its own refresh mechanism via admin-ajax action
                // 'get_refreshed_fragments' + window.updateFragments() function.
                // Feature-gated: runs only if the theme's globals are present (silently no-ops
                // on other themes so plugin remains portable).
                try {
                    const donkeyParams = window.donkey_cart_params;
                    const updateFragmentsFn = window.updateFragments;
                    if (donkeyParams?.ajax_url && typeof updateFragmentsFn === 'function') {
                        const donkeyRes = await fetch(donkeyParams.ajax_url, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                            body: 'action=get_refreshed_fragments',
                            credentials: 'same-origin',
                        });
                        const donkeyData = await donkeyRes.json();
                        if (donkeyData?.success && donkeyData.data?.fragments) {
                            updateFragmentsFn(donkeyData.data.fragments);
                            console.log('[WooAI] Donkey theme cart drawer refreshed via theme ajax action');
                        }
                    }
                }
                catch (e) {
                    console.warn('[WooAI] Donkey theme cart refresh failed:', e);
                }
                // Trigger WC events (jQuery-based themes)
                const jq = window.jQuery;
                if (jq) {
                    if (hadAdd)
                        jq(document.body).trigger('added_to_cart');
                    if (hadRemove)
                        jq(document.body).trigger('removed_from_cart');
                    jq(document.body).trigger('wc_fragment_refresh');
                }
                // Update cart count in header (shared utility — same selectors as streaming path)
                await updateCartCountInHeader();
                // Second update after 500ms to override any fragment refresh that might revert the count
                setTimeout(updateCartCountInHeader, 500);
                setCartFeedback({
                    status: 'success',
                    message: hadRemove && !hadAdd ? '✓ Removed from cart' : '✓ Added to cart',
                });
                setCartStatus('done');
            }
            catch (err) {
                console.error('[WooAI] Cart action failed:', err);
                setCartFeedback({ status: 'error', message: 'Could not update cart. Try from the product page.' });
                setCartStatus('error');
            }
        };
        executeCartActions();
    }, [hasCartActions]);
    // Prefer message.products (merged+filtered by backend) over individual tool_results.
    // The REST chat endpoint nests recommendation/search products inside
    // message.metadata.products (and tool_results inside message.metadata.tool_results),
    // not at the message level — without this fallback the ProductCarousel never
    // rendered for recommendation replies (bot mentioned the table by name but the
    // card was missing). Lift from metadata when message.* is empty.
    /* eslint-disable @typescript-eslint/no-explicit-any */
    const meta = message.metadata || {};
    const toolResultsSource = message.tool_results && message.tool_results.length
        ? message.tool_results
        : Array.isArray(meta.tool_results)
            ? meta.tool_results
            : [];
    const toolProducts = toolResultsSource
        .filter((tr) => tr && tr.tool === 'search_products' && Array.isArray(tr.result?.products))
        .flatMap((tr) => tr.result?.products || []);
    const metaProducts = Array.isArray(meta.products) ? meta.products : [];
    const products = message.products?.length
        ? message.products
        : metaProducts.length
            ? metaProducts
            : toolProducts;
    const hasProducts = products.length > 0;
    /* eslint-enable @typescript-eslint/no-explicit-any */
    if (hasProducts) {
        console.log('[WooAI] RENDERING PRODUCTS:', products);
    }
    // In RTL: bot messages align right, in LTR: bot messages align left
    // Bot icon appears on right in RTL, left in LTR
    const botFlexDirection = isRTL ? 'flex-row-reverse' : 'flex-row';
    const botRoundedCorner = isRTL ? 'rounded-br-md' : 'rounded-bl-md';
    // Avatar width (w-8 = 32px) + gap-3 (12px) = 44px offset that shifts content right.
    // When showing the carousel we need to break out of that indent so it spans full width.
    // We compensate with a negative margin equal to the avatar+gap size.
    const avatarPlusGap = 44; // 32px avatar + 12px gap
    return (_jsxs("div", { className: wac('flex flex-col'), children: [_jsxs("div", { className: wac(`flex gap-3 ${botFlexDirection}`), children: [_jsx("div", { className: wac('w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0'), style: { background: 'var(--waa-primary-50)' }, children: _jsx(MessageSquare, { className: wac('w-4 h-4'), style: { color: 'var(--waa-primary)' } }) }), _jsxs("div", { className: wac('flex flex-col'), style: { maxWidth: '75%' }, children: [_jsx("div", { dir: "auto", className: wac(`px-4 py-3 rounded-2xl ${botRoundedCorner} text-[15px] leading-relaxed`), style: {
                                    background: 'var(--waa-chat-bot-bg)',
                                    color: 'var(--waa-chat-bot-text)',
                                }, 
                                // eslint-disable-next-line react/no-danger
                                dangerouslySetInnerHTML: { __html: parseMarkdown(message.content) } }), cartFeedback && (_jsx("div", { style: {
                                    fontSize: '12px',
                                    padding: '4px 8px',
                                    marginTop: '6px',
                                    borderRadius: '999px',
                                    backgroundColor: cartFeedback.status === 'success' ? 'var(--waa-primary-50, #eef2ff)' : 'rgba(239,68,68,0.08)',
                                    color: cartFeedback.status === 'success' ? 'var(--waa-primary, #6366f1)' : 'var(--waa-error, #ef4444)',
                                }, children: cartFeedback.message }))] })] }), hasCart && cartData && (_jsx("div", { className: wac('mt-3'), style: isRTL
                    ? { marginInlineEnd: `${avatarPlusGap}px` }
                    : { marginInlineStart: `${avatarPlusGap}px` }, children: _jsx(CartView, { cart: cartData }) })), hasCartActions && (_jsxs("div", { className: wac('mt-2 flex flex-col gap-2'), style: isRTL
                    ? { marginInlineEnd: `${avatarPlusGap}px` }
                    : { marginInlineStart: `${avatarPlusGap}px` }, children: [cartStatus === 'adding' && (_jsxs("div", { className: wac('flex items-center gap-1.5 text-sm'), style: { color: 'var(--waa-gray-600)' }, children: [_jsx(Loader2, { className: wac('w-4 h-4 animate-spin') }), t('addingToCart') || 'Adding to cart...'] })), cartStatus === 'done' && (_jsxs(_Fragment, { children: [_jsxs("div", { className: wac('inline-flex items-center gap-1.5 text-sm font-semibold'), style: {
                                    padding: '6px 14px',
                                    background: 'var(--waa-secondary, #10b981)',
                                    color: '#fff',
                                    borderRadius: '999px',
                                }, children: [_jsx(Check, { className: wac('w-4 h-4') }), t('addedToCart') || 'Added to cart!'] }), _jsxs("div", { className: wac('flex gap-2 flex-wrap'), children: [cartUrl && (_jsxs("a", { href: cartUrl, target: "_blank", rel: "noopener noreferrer", className: wac('flex items-center gap-2 text-sm font-semibold transition-opacity hover:opacity-80 no-underline'), style: {
                                            padding: '8px 20px',
                                            border: '1.5px solid var(--waa-primary, #6366F1)',
                                            color: 'var(--waa-primary, #6366F1)',
                                            background: 'transparent',
                                            borderRadius: '999px',
                                            fontFamily: 'inherit',
                                        }, children: [_jsx(ShoppingCart, { className: wac('w-4 h-4') }), t('viewCart')] })), checkoutUrl && (_jsxs("a", { href: checkoutUrl, target: "_blank", rel: "noopener noreferrer", className: wac('flex items-center gap-2 text-sm font-semibold transition-opacity hover:opacity-90 no-underline'), style: {
                                            padding: '8px 20px',
                                            background: 'var(--waa-primary, #6366F1)',
                                            color: '#fff',
                                            borderRadius: '999px',
                                            fontFamily: 'inherit',
                                        }, children: [_jsx(CreditCard, { className: wac('w-4 h-4') }), t('checkout')] }))] })] })), cartStatus === 'error' && (_jsx("div", { className: wac('text-sm'), style: { color: 'var(--waa-error, #dc2626)' }, children: t('cartError') || 'Failed to add items to cart. Please try again.' }))] })), hasProducts && onAddToCart && (_jsx("div", { className: wac('mt-3'), style: {
                    // Break out of the messages container padding (24px left + 32px right → use 32px symmetric)
                    marginInlineStart: '-32px',
                    marginInlineEnd: '-32px',
                    width: 'calc(100% + 64px)',
                    minWidth: 0,
                    overflow: 'hidden',
                }, children: _jsx(ProductCarousel, { products: products, onAddToCart: onAddToCart, sessionId: sessionId, showCarouselArrows: window.wooAIChatConfig?.appearance?.showCarouselArrows === true, carouselArrowSvg: window.wooAIChatConfig?.appearance?.carouselArrowSvg }) })), message.timestamp && (_jsx("div", { className: wac('text-[11px] mt-1'), style: {
                    color: 'var(--waa-gray-500)',
                    ...(isRTL
                        ? { marginInlineEnd: `${avatarPlusGap}px` }
                        : { marginInlineStart: `${avatarPlusGap}px` }),
                }, children: message.timestamp }))] }));
}
